import React, { useCallback, useEffect, useState } from "react";
import { Button, Popconfirm, Spin, message } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import CollapsibleFilePreview from "./CollapsibleFilePreview";
import ProtectedFilePreview from "./ProtectedFilePreview";
import SubmissionResultForm from "./SubmissionResultForm";
import {
  claimFormUrl,
  deleteSubmissionResult,
  fetchPolicyRequestedEstimates,
  resultDocumentUrl,
} from "./estimateClaimsApi";
import { requestTypeMeta } from "../../../InsuranceRequests/requestTypes";
import {
  formatDay, isAnswer, isFollowUp, isOfficeOutcome, outcomeChipStyle, outcomeLabel,
} from "../../../InsuranceRequests/outcomes";
import { describeApiError } from "../../lib/apiError";
import "./estimateClaims.css";

/**
 * What the office asked us to chase on this policy, and what came back on each
 * submission — one card per submission, the ask on the left and the answers on
 * the right, so nobody has to guess which result belongs to which request.
 *
 * An estimate task used to open with a policy and nothing else, so whoever picked
 * it up had to go back to the office to ask what was submitted. The left column
 * is that missing half: amount, date of service, patient, provider and the
 * CDA/iTrans number, the procedures, and the claim form itself when one was
 * uploaded, readable in place. The right column is the FTC's side: each
 * recorded result ("<carrier> contacted on <date>": procedures, claim / eligible
 * / payable / paid, note, the carrier's response), oldest first so the newest
 * reads last, and — for staff — the Record result button. Completion is per submission; the request
 * closes, and the office is emailed, when the last one is done.
 *
 * Each entry says what happens next (its outcome), and the newest one IS the
 * submission's state, which the card head shows: Done; "Follow-up on <day>"
 * (off the FTC queue until then); "Waiting on the office" (with them until they
 * resubmit — their reply then appears here as an entry of their own); else
 * "Awaiting result".
 *
 * It lists every request of the type on the policy, newest first, each with its
 * status: the open one is what still needs doing; a fulfilled one is what the
 * office submitted behind the result it is now looking at. Only the newest
 * request is expanded; older ones fold to a line until asked for.
 *
 * The office's side is read-only by design — the office states what it
 * submitted; the FTC chases it — and `readOnly` (the modal's viewer mode) hides
 * the FTC's controls too. Renders NOTHING when the policy has no request of the
 * type (unless the host passes `emptyText`).
 *
 * `requestType` picks which submission-bearing request to show — "estimate"
 * (default) or "claim_followup". The two can be open on one policy at once, so
 * the Estimates tab never shows a claim's rows and the Claims tab never shows an
 * estimate's. `refreshKey` re-reads the rows when the host knows they changed
 * (both tabs bump it after the composer sends a request). `onPolicyPatched`
 * receives the per-policy patch when a result fulfilled the request, so the
 * outcome buttons and the board update through the path they use today.
 */
export default function RequestedEstimatesPanel({
  policyId,
  requestType = "estimate",
  emptyText,
  refreshKey,
  readOnly = false,
  carrierName,
  patientId,
  onPolicyPatched,
}) {
  const [state, setState] = useState({ loading: false, requests: [] });
  // Older requests the user has unfolded, by request id.
  const [unfolded, setUnfolded] = useState({});
  // The result form: { request, estimate, initial } while open.
  const [form, setForm] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const isClaim = requestType === "claim_followup";
  const noun = requestTypeMeta(requestType).submissionNoun || "estimate";
  const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);

  const load = useCallback(
    async function () {
      // The fetch itself lives in estimateClaimsApi so every reader of these
      // rows reads them the same way. It already swallows errors into an empty
      // list — silent by design, since this is supporting context and a banner
      // here would sit on top of the work the user actually came to do.
      const body = await fetchPolicyRequestedEstimates(policyId, { requestType: requestType, scope: "all" });
      // A request without rows has nothing to show — legacy rows opened by a
      // tag write can be like that.
      return (body.requests || []).filter(function (r) {
        return Array.isArray(r.estimates) && r.estimates.length > 0;
      });
    },
    [policyId, requestType],
  );

  useEffect(
    function () {
      if (!policyId) {
        setState({ loading: false, requests: [] });
        return undefined;
      }
      let cancelled = false;
      setState(function (s) { return Object.assign({}, s, { loading: true }); });
      load().then(function (requests) {
        if (cancelled) return;
        setState({ loading: false, requests: requests });
        setUnfolded({});
      });
      return function () { cancelled = true; };
    },
    [policyId, load, refreshKey],
  );

  // After any write: re-read the rows (one GET keeps every card consistent),
  // hand the policy patch up when this write fulfilled the request, and say
  // what the outcome did — where the work went is the thing worth telling.
  async function afterWrite(response, verb) {
    const requests = await load();
    setState({ loading: false, requests: requests });
    const saved = (response && response.result) || null;
    if (response && response.fulfilled_now && typeof onPolicyPatched === "function" && response.policy) {
      onPolicyPatched(policyId, response.policy);
    }
    if (verb === "deleted") {
      message.success("Result deleted.");
    } else if (response && response.fulfilled_now) {
      message.success(
        (isClaim ? "Claim follow-up complete" : "Estimate complete") + " — the office has been emailed.",
      );
    } else if (saved && isOfficeOutcome(saved.outcome)) {
      message.success("Sent to the office — they've been emailed, and it's off your queue until they resubmit.");
    } else if (saved && isFollowUp(saved.outcome)) {
      message.success(outcomeLabel(saved.outcome) + " — back in your queue on " + formatDay(saved.follow_up_on) + ".");
    } else if (saved && isAnswer(saved.outcome)) {
      message.success(outcomeLabel(saved.outcome) + " — the office has been emailed.");
    } else {
      message.success("Result saved.");
    }
  }

  async function handleDelete(result) {
    setDeletingId(result.id);
    try {
      const res = await deleteSubmissionResult(result.id);
      if (res && res.status >= 200 && res.status < 300) {
        await afterWrite(res.json, "deleted");
      } else {
        message.error(describeApiError(res, "Couldn't delete the result."));
      }
    } finally {
      setDeletingId(null);
    }
  }

  if (state.loading && !state.requests.length) {
    return (
      <div style={{ padding: "8px 0", textAlign: "center" }}>
        <Spin size="small" />
      </div>
    );
  }
  if (!state.requests.length) {
    if (!emptyText || !policyId) return null;
    return (
      <div style={{ textAlign: "center", padding: "30px 20px", color: "#848696", fontSize: 13 }}>
        {emptyText}
      </div>
    );
  }

  function titleFor(req) {
    const many = req.estimates.length > 1;
    if (isClaim) {
      return req.status === "open"
        ? "Claim" + (many ? "s" : "") + " to follow up"
        : "Claim" + (many ? "s" : "") + " followed up";
    }
    return "Requested estimate" + (many ? "s" : "");
  }

  function statusChip(req) {
    if (req.status !== "open") {
      return "Done" + (req.fulfilled_at ? " · " + dayjs(req.fulfilled_at).format("MMM D, YYYY") : "");
    }
    const total = req.submissions_total != null ? req.submissions_total : req.estimates.length;
    const done = req.submissions_done != null ? req.submissions_done : 0;
    return total > 1 ? `Open · ${done} of ${total} done` : "Open";
  }

  function lastOpenInRequest(req, est) {
    return req.status === "open" && !req.estimates.some(function (e) {
      return e.id !== est.id && !e.completed_at;
    });
  }

  function money(v) {
    return v === null || v === undefined || v === "" ? null : "$" + v;
  }

  // What one entry said — a chip beside its title, in the outcome's colours.
  function outcomeChip(result) {
    const label = outcomeLabel(result.outcome);
    return {
      style: outcomeChipStyle(result.outcome),
      text: isFollowUp(result.outcome) && result.follow_up_on
        ? label + " · " + formatDay(result.follow_up_on, false)
        : label,
    };
  }

  // The office's own entry — its reply on resubmit. Read-only for everyone:
  // it is the office's word, and the server refuses to edit or delete it.
  function renderOfficeReply(result) {
    const when = result.created_at ? dayjs(result.created_at).format("MMM D, YYYY") : "";
    return (
      <div key={result.id} className="est-result est-result--office">
        <div className="est-result-head">
          <span className="est-result-title">
            {readOnly ? "Your office replied" : "The office replied"}{when ? " on " + when : ""}
          </span>
        </div>
        <div className="est-result-body">
        {result.note ? (
          <div className="est-result-note">
            <div className="est-view-value">{result.note}</div>
          </div>
        ) : (
          <div className="est-view-value est-view-value--empty">Resubmitted without a note.</div>
        )}
        {/* The file the office resubmitted with — a corrected claim form, the
            EOB asked for — read in place, where the FTC follows it up. */}
        {result.has_document ? (
          <div className="est-view-form">
            <div className="est-view-label">Resubmitted file</div>
            <ProtectedFilePreview
              url={resultDocumentUrl(result.id)}
              fileName={result.document_name}
              label="resubmitted-file"
              height={420}
            />
          </div>
        ) : null}
        <div className="est-result-meta">
          {result.recorded_by ? "By " + result.recorded_by + " · " : ""}
          {result.created_at ? dayjs(result.created_at).format("MMM D, YYYY h:mm A") : ""}
        </div>
        </div>
      </div>
    );
  }

  // `isLatest`: this is the item's newest entry, so its chip is the status the
  // item has now; every earlier card's chip is only what that entry set.
  function renderResult(req, est, result, isLatest) {
    if (result.outcome === "office_reply") return renderOfficeReply(result);
    const when = result.contacted_on ? dayjs(result.contacted_on).format("MMM D, YYYY") : "an unknown date";
    const chip = outcomeChip(result);
    return (
      <div key={result.id} className="est-result">
        <div className="est-result-head">
          <span className="est-result-title">{carrierName || "Carrier"} contacted on {when}</span>
          <span className="est-result-status">
            <span className="est-result-status-label">{isLatest ? "Latest status update:" : "Status update:"}</span>
            <span className="est-claims-ordinal est-claims-ordinal--state" style={chip.style}>{chip.text}</span>
          </span>
          {!readOnly ? (
            <span className="est-result-actions">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                aria-label="Edit result"
                onClick={function () { setForm({ request: req, estimate: est, initial: result }); }}
              />
              <Popconfirm
                title="Delete this result?"
                description="The office will no longer see it."
                okText="Delete"
                okButtonProps={{ danger: true, loading: deletingId === result.id }}
                cancelText="Keep it"
                onConfirm={function () { handleDelete(result); }}
              >
                <Button type="text" size="small" danger icon={<DeleteOutlined />} aria-label="Delete result" loading={deletingId === result.id} />
              </Popconfirm>
            </span>
          ) : null}
        </div>

        <div className="est-result-body">
        {(result.procedures || []).length ? (
          <ul className="est-result-lines">
            {result.procedures.map(function (p, i) {
              return (
                <li key={p.code + ":" + i}>
                  Procedure: {p.description ? p.description + " " : ""}{p.code}{p.tooth ? " - " + p.tooth : ""}
                </li>
              );
            })}
          </ul>
        ) : null}

        <div className="est-view-grid">
          <Field label="Claim amount" value={money(result.claim_amount)} />
          <Field label="Eligible amount" value={money(result.eligible_amount)} />
          <Field label="Payable amount" value={money(result.payable_amount)} />
          {isClaim ? <Field label="Paid amount" value={money(result.paid_amount)} /> : null}
        </div>

        {result.note ? (
          <div className="est-result-note">
            <div className="est-view-label">Note</div>
            <div className="est-view-value">{result.note}</div>
          </div>
        ) : null}

        {result.has_document ? (
          <div className="est-view-form">
            <div className="est-view-label">Carrier's response</div>
            <ProtectedFilePreview
              url={resultDocumentUrl(result.id)}
              fileName={result.document_name}
              label="carrier-response"
              height={420}
            />
          </div>
        ) : null}

        <div className="est-result-meta">
          {result.recorded_by ? "Recorded by " + result.recorded_by + " · " : "Recorded "}
          {result.created_at ? dayjs(result.created_at).format("MMM D, YYYY h:mm A") : ""}
        </div>
        </div>
      </div>
    );
  }

  // The item's state, from its newest entry (the server derives it): the
  // answer itself once it is in ("Covered · Sep 30, 2026"), what it waits on
  // otherwise.
  function stateChip(est) {
    if (est.completed_at || est.state === "done") {
      const answered = isAnswer(est.outcome) ? est.outcome : "done";
      return {
        style: outcomeChipStyle(answered),
        text: outcomeLabel(answered) + (est.completed_at ? " · " + dayjs(est.completed_at).format("MMM D, YYYY") : ""),
      };
    }
    if (est.state === "waiting_on_office") {
      const why = isOfficeOutcome(est.outcome) ? est.outcome : "missing_info";
      return { style: outcomeChipStyle(why), text: outcomeLabel(why) };
    }
    if (est.state === "follow_up") {
      const why = isFollowUp(est.outcome) ? est.outcome : "follow_up_again";
      return { style: outcomeChipStyle(why), text: outcomeLabel(why) + " · " + formatDay(est.follow_up_on, false) };
    }
    return { style: undefined, text: "Awaiting result" };
  }

  // An item opened without details (a tag edit on the Patients board) is still
  // answered here; its left column says so instead of listing empty fields.
  function isBlank(est) {
    return !est.amount && !est.service_date && !est.patient_name && !est.provider_name
      && !est.cda_itrans_number && !(est.procedures || []).length && !est.has_claim_form;
  }

  function renderSubmission(req, est, index) {
    // The server sends newest first (the item's state is its newest entry);
    // the reader gets them as a timeline, oldest at the top, newest at the end.
    const results = est.results || [];
    const newestId = results.length ? results[0].id : null;
    const timeline = results.slice().reverse();
    const summary = [
      est.amount ? "$" + est.amount : null,
      est.service_date ? "Service " + est.service_date : null,
      est.patient_name || null,
    ].filter(Boolean).join(" · ");
    const chip = stateChip(est);
    return (
      <div key={est.id} className="est-sub">
        <div className="est-sub-head">
          <span className="est-sub-ordinal">{Noun} {index + 1}</span>
          {summary ? <span className="est-sub-summary">{summary}</span> : null}
          <span className="est-claims-ordinal est-claims-ordinal--state" style={chip.style}>{chip.text}</span>
        </div>

        {/* Two halves of one submission, each under its own band so the ask and
            the answer read as a pair — "this is what was sent; this is what
            came back" — rather than two loose lists. */}
        <div className="est-sub-cols">
          <section className="est-sub-col">
            <div className="est-sub-col-head">
              <span className="est-sub-col-title">Requested by the office</span>
              {est.has_claim_form ? <span className="est-sub-col-meta">Claim form attached</span> : null}
            </div>
            <div className="est-sub-col-body">
            {isBlank(est) ? (
              <div className="est-view-value est-view-value--empty" style={{ fontSize: 13 }}>
                No details were given with this request — it was opened from the Patients board. Record what the
                carrier said about this {noun} on the right.
              </div>
            ) : (
            <>
            <div className="est-view-grid">
              <Field label="Amount" value={money(est.amount)} />
              <Field label="Date of service" value={est.service_date} />
              <Field label="Patient" value={est.patient_name} />
              <Field label="Provider" value={est.provider_name} />
              <Field label="CDA / iTrans #" value={est.cda_itrans_number} />
            </div>

            {/* What was actually claimed. Absent on estimates recorded before
                procedures were collected, so it renders nothing rather than an
                empty row. */}
            {(est.procedures || []).length ? (
              <div className="est-view-procs">
                <div className="est-view-label">Procedures</div>
                <div className="est-view-proc-list">
                  {est.procedures.map(function (proc, i) {
                    return (
                      <span className="est-view-proc" key={proc.code + ":" + i}>
                        <span className="est-view-proc-code">{proc.code}</span>
                        {proc.tooth ? <span className="est-view-proc-tooth">tooth {proc.tooth}</span> : null}
                      </span>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {est.has_claim_form ? (
              <div className="est-view-form">
                <div className="est-view-label">Claim form</div>
                <CollapsibleFilePreview
                  url={claimFormUrl(est.id)}
                  fileName={est.claim_form_name}
                  label="claim-form"
                  height={560}
                  showText="Show claim form"
                  hideText="Hide claim form"
                />
              </div>
            ) : (
              <div className="est-view-value est-view-value--empty" style={{ marginTop: 8, fontSize: 12 }}>
                No claim form was attached — the details above were entered by the office.
              </div>
            )}
            </>
            )}
            </div>
          </section>

          <section className="est-sub-col est-sub-col--results">
            <div className="est-sub-col-head">
              <span className="est-sub-col-title">Result{results.length === 1 ? "" : "s"}</span>
              <span className="est-sub-col-meta">
                {results.length === 0
                  ? "Nothing recorded yet"
                  : results.length === 1
                    ? "1 recorded"
                    : results.length + " recorded"}
              </span>
            </div>
            <div className="est-sub-col-body">
            {timeline.map(function (r) { return renderResult(req, est, r, r.id === newestId); })}
            {!results.length ? (
              <div className="est-result-empty">
                {readOnly ? "No result recorded yet." : "No result recorded yet — record what the carrier said."}
              </div>
            ) : null}
            {!readOnly ? (
              <div className="est-result-record">
                <Button
                  type={results.length ? "default" : "primary"}
                  icon={<PlusOutlined />}
                  onClick={function () { setForm({ request: req, estimate: est, initial: null }); }}
                >
                  {results.length ? "Record another result" : "Record result"}
                </Button>
              </div>
            ) : null}
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="est-view">
      {state.requests.map(function (req, index) {
        const isOpen = req.status === "open";
        // The newest request is always shown in full; anything older is history
        // the reader asks for.
        const shown = index === 0 || !!unfolded[req.id];
        return (
          <div key={req.id} className={"est-view-request" + (shown ? "" : " is-folded")}>
            <div className="est-claims-policy-head est-view-request-head">
              <span className="est-claims-carrier">{titleFor(req)}</span>
              {isOpen && req.urgency === "urgent" ? (
                <span className="est-claims-ordinal est-claims-ordinal--urgent">Urgent</span>
              ) : null}
              <span className={"est-claims-ordinal " + (isOpen ? "est-claims-ordinal--open" : "est-claims-ordinal--done")}>
                {statusChip(req)}
              </span>
              <span className="est-view-when">
                {req.requested_at ? "Requested " + dayjs(req.requested_at).format("MMM D, YYYY") : ""}
              </span>
              {index > 0 ? (
                <button
                  type="button"
                  className="est-view-toggle"
                  onClick={function () {
                    setUnfolded(function (prev) {
                      return Object.assign({}, prev, { [req.id]: !prev[req.id] });
                    });
                  }}
                >
                  {shown ? "Hide" : "Show"}
                </button>
              ) : null}
            </div>

            {shown ? req.estimates.map(function (est, i) { return renderSubmission(req, est, i); }) : null}
          </div>
        );
      })}

      {form ? (
        <SubmissionResultForm
          open
          onClose={function () { setForm(null); }}
          estimate={form.estimate}
          requestType={requestType}
          carrierName={carrierName}
          patientId={patientId}
          initial={form.initial}
          lastOpenInRequest={lastOpenInRequest(form.request, form.estimate)}
          onSaved={async function (response) {
            setForm(null);
            await afterWrite(response, "saved");
          }}
        />
      ) : null}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="est-view-label">{label}</div>
      <div className={"est-view-value" + (value ? "" : " est-view-value--empty")}>
        {value || "Not provided"}
      </div>
    </div>
  );
}
